import io from 'socket.io-client';
import { addMessage } from '../slices/messageSlice';
import {
  updateLastMessage,
  updateConversationUnreadCount,
  fetchConversations,
} from '../slices/conversationSlice';
import {
  markAsReadLocal,
  updateUnreadCount,
  addNewNotification,
} from '../notificationsSlice';
import storage from '../../utils/storage';



const SOCKET_URL = process.env.REACT_APP_SOCKET_BASE_URL || 'https://prod.api.go-playapp.com';


let socketInitialized = false;

const socketMiddleware = () => {
  let socket = null;
  let isConnecting = false;
  let currentUserId = null;

  const removeAllListeners = (socketInstance) => {
    if (!socketInstance) return;
    [
      'connect',
      'getMessage',
      'unreadCount',
      'unreadCountUpdate',
      'unreadMessageCount',
      'notificationRead',
      'newNotification',
      'allNotificationsRead',
      'disconnect',
      'connect_error',
      'error',
    ].forEach((event) => socketInstance.removeAllListeners(event));
  };

  const setupEventListeners = (socketInstance, userId, dispatch, getState) => {
    socketInstance.on('connect', () => {
      console.log('SOCKET: Connected', socketInstance.id, userId);
      socketInstance.emit('addUser', userId);
      socketInstance.emit('getUnreadCount', { userId });
    });

    socketInstance.on('getMessage', async (data) => {
      const state = getState();
      const me =
        state.auth.user?._id || (await storage.getItem('userId'));

      if (!data?.sender || !data?.text) return;

      const isFromCurrentUser = String(data.sender) === String(me);
      const list = state.conversations.conversations || [];

      let conversation = null;

      if (data.conversationId) {
        conversation = list.find(
          (c) =>
            String(c.id) === String(data.conversationId) ||
            String(c._id) === String(data.conversationId)
        );
      }

      if (!conversation) {
        conversation = list.find((conv) => {
          if (!conv.members || conv.members.length !== 2) return false;
          const memberIds = conv.members.map((m) =>
            String(typeof m === 'object' && m !== null ? m._id : m)
          );
          return (
            memberIds.includes(String(data.sender)) &&
            memberIds.includes(String(me))
          );
        });
      }

      if (!conversation) {
        dispatch(fetchConversations());
        return;
      }

      const conversationId = conversation.id;

      if (!isFromCurrentUser) {
        dispatch(
          addMessage({
            id: data._id || Date.now(),
            sender: data.senderName || 'User',
            text: data.text,
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            isMe: false,
            conversationId,
            senderId: data.sender,
          })
        );
      }

      dispatch(
        updateLastMessage({
          conversationId,
          message: data.text,
          time: new Date().toISOString(),
          senderId: data.sender,
          senderName: isFromCurrentUser ? 'Me' : data.senderName || 'User',
          isFromCurrentUser,
        })
      );
    });

    socketInstance.on('unreadCount', (data) => {
      const state = getState();
      const localUnread = state.notifications.items.filter(
        (n) => n.status === 'unread'
      ).length;

      if (!data?.success || data.count === undefined) return;

      if (localUnread === 0 && data.count > 0) {
        dispatch(updateUnreadCount(data.count));
      } else if (Math.abs(data.count - localUnread) <= 2) {
        dispatch(updateUnreadCount(data.count));
      }
    });

    socketInstance.on('unreadCountUpdate', (data) => {
      if (!data?.conversationId) return;
      dispatch(
        updateConversationUnreadCount({
          conversationId: data.conversationId,
          unreadCount: data.unreadCount,
        })
      );
    });

    socketInstance.on('notificationRead', (data) => {
      if (data?.notificationId) {
        dispatch(markAsReadLocal(data.notificationId));
      }
    });

    socketInstance.on('newNotification', (data) => {
      dispatch(addNewNotification(data));
    });

    socketInstance.on('allNotificationsRead', () => {
      dispatch(updateUnreadCount(0));
    });

    socketInstance.on('disconnect', async (reason) => {
      console.log('SOCKET: Disconnected', reason);
      if (reason === 'io server disconnect' || reason === 'transport close') {
        const state = getState();
        const storedUserId =
          state.auth.user?._id || (await storage.getItem('userId'));
        if (storedUserId && storedUserId === currentUserId && !isConnecting) {
          isConnecting = true;
          setTimeout(() => {
            isConnecting = false;
            dispatch({
              type: 'socket/connect',
              payload: { userId: storedUserId },
            });
          }, 1000);
        }
      }
    });

    socketInstance.on('connect_error', (error) => {
      console.error('SOCKET: connect_error', error.message);
      isConnecting = false;
    });

    socketInstance.on('error', (error) => {
      console.error('SOCKET: error', error.message);
    });
  };

  return (store) => (next) => (action) => {
    const { dispatch, getState } = store;

    switch (action.type) {
      case 'socket/connect': {
        const userId = action.payload?.userId;
        if (!userId) break;

        if (socket?.connected && currentUserId === userId) {
          socketInitialized = true;
          break;
        }

        if (socket && !socket.connected && isConnecting) break;

        if (socket && !socket.connected && currentUserId === userId) {
          if (!socket.io.reconnecting && !isConnecting) socket.connect();
          socketInitialized = true;
          break;
        }

        if (socket && currentUserId && currentUserId !== userId) {
          removeAllListeners(socket);
          socket.close();
          socket = null;
          currentUserId = null;
          socketInitialized = false;
          isConnecting = false;
        }

        if (isConnecting) break;

        if (socketInitialized && currentUserId === userId && socket) {
          if (!socket.connected && !socket.io.reconnecting && !isConnecting) {
            socket.connect();
          }
          break;
        }

        isConnecting = true;
        currentUserId = userId;

        socket = io(SOCKET_URL, {
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          autoConnect: true,
          query: { userId },
        });

        setupEventListeners(socket, userId, dispatch, getState);

        socket.once('connect', () => {
          isConnecting = false;
          socketInitialized = true;
        });

        socket.once('connect_error', () => {
          isConnecting = false;
        });

        break;
      }

      case 'socket/disconnect': {
        if (socket) {
          removeAllListeners(socket);
          socket.close();
          socket = null;
          currentUserId = null;
          isConnecting = false;
          socketInitialized = false;
        }
        break;
      }

      case 'socket/sendMessage': {
        if (socket) {
          socket.emit('sendMessage', action.payload);
        } else {
          console.warn('SOCKET: not connected, cannot send');
        }
        break;
      }

      default:
        break;
    }

    return next(action);
  };
};

export const socketActions = {
  connect: (userId) => ({ type: 'socket/connect', payload: { userId } }),
  disconnect: () => ({ type: 'socket/disconnect' }),
  sendMessage: (messageData) => ({
    type: 'socket/sendMessage',
    payload: messageData,
  }),
};

export default socketMiddleware;